import React, { useState } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { Save, Loader2, CircleSlash, ArrowRight } from 'lucide-react';

const C = { navy: '#0D1B2A', slate: '#5C6B7A', green: '#1D9E75', amber: '#E8A020', red: '#E24B4A', blue: '#1A4C7C' };
const sel = "w-full text-[11px] rounded-sm border px-1.5 py-1";
const border = { borderColor: 'rgba(136,150,168,0.3)', color: C.navy };

function ValueMap({ row, targets, onChange }) {
  const map = row.options?.map || {};
  const keys = Object.keys(map);
  if (!keys.length) return <p className="text-[10px]" style={{ color: C.slate }}>No source values were observed for this column.</p>;
  return (
    <div className="space-y-1 pl-2 border-l-2" style={{ borderColor: 'rgba(26,76,124,0.2)' }}
      data-testid={`value-map-${row.canonical_field}`}>
      {keys.slice(0, 14).map(k => (
        <div key={k} className="flex items-center gap-1.5">
          <span className="text-[10px] font-mono w-40 truncate" style={{ color: C.slate }} title={k}>{k}</span>
          <ArrowRight className="w-3 h-3 shrink-0" style={{ color: map[k] ? C.green : C.red }} />
          {targets.length ? (
            <select value={map[k] || ''} className={sel} style={{ ...border, width: 190 }}
              data-testid={`value-map-target-${row.canonical_field}-${k}`}
              onChange={e => onChange({ ...row.options, map: { ...map, [k]: e.target.value } })}>
              <option value="">— decide —</option>
              {targets.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
            </select>
          ) : (
            <Input value={map[k] || ''} className="h-6 text-[11px] rounded-sm w-48"
              onChange={e => onChange({ ...row.options, map: { ...map, [k]: e.target.value } })} />
          )}
        </div>
      ))}
      <div className="flex items-center gap-1.5">
        <span className="text-[10px]" style={{ color: C.slate }}>anything else</span>
        <select value={row.options?.fallback || 'reject'} className={sel} style={{ ...border, width: 140 }}
          data-testid={`value-map-fallback-${row.canonical_field}`}
          onChange={e => onChange({ ...row.options, fallback: e.target.value })}>
          <option value="reject">reject the row</option>
          <option value="blank">leave blank</option>
          <option value="passthrough">pass through as-is</option>
        </select>
      </div>
    </div>
  );
}

function Options({ row, targets, onChange }) {
  if (row.transform === 'value_map') return <ValueMap row={row} targets={targets} onChange={onChange} />;
  if (row.transform === 'constant') return (
    <Input value={row.options?.value || ''} placeholder="fixed value" className="h-6 text-[11px] rounded-sm w-44"
      data-testid={`constant-${row.canonical_field}`}
      onChange={e => onChange({ ...row.options, value: e.target.value })} />
  );
  if (row.transform === 'date_iso') return (
    <select value={row.options?.source_format || 'auto'} className={sel} style={{ ...border, width: 150 }}
      data-testid={`date-format-${row.canonical_field}`}
      onChange={e => onChange({ ...row.options, source_format: e.target.value })}>
      {['auto', 'DD/MM/YYYY', 'MM/DD/YYYY', 'YYYY-MM-DD', 'DD-MMM-YYYY'].map(f => <option key={f} value={f}>{f}</option>)}
    </select>
  );
  if (row.transform === 'split_take') return (
    <div className="flex gap-1">
      <Input value={row.options?.separator || ''} placeholder="separator" className="h-6 text-[11px] rounded-sm w-20"
        onChange={e => onChange({ ...row.options, separator: e.target.value })} />
      <Input type="number" value={row.options?.index ?? 0} className="h-6 text-[11px] rounded-sm w-14"
        onChange={e => onChange({ ...row.options, index: Number(e.target.value) })} />
    </div>
  );
  return null;
}

function describe(row) {
  const o = row.options || {};
  if (row.transform === 'value_map') {
    const map = o.map || {};
    const decided = Object.values(map).filter(Boolean).length;
    const fallback = { reject: 'anything else rejected', blank: 'anything else left blank', passthrough: 'anything else passed through' }[o.fallback || 'reject'];
    return `${decided} of ${Object.keys(map).length} values mapped · ${fallback}`;
  }
  if (row.transform === 'date_iso') return `source format ${o.source_format || 'auto'}`;
  if (row.transform === 'constant') return `always “${o.value ?? ''}”`;
  if (row.transform === 'split_take') return `split on “${o.separator || ' '}”, take part ${o.index ?? 0}`;
  if (row.transform === 'concat') return `join ${(o.columns || []).join(' + ')}`;
  return '—';
}

export function MappingSetEditor({ api, set, transforms, targetsFor, onSaved, isAdmin }) {
  const [rows, setRows] = useState(set.rows || []);
  const [workflow, setWorkflow] = useState(set.workflow || null);
  const [busy, setBusy] = useState(false);
  const frozen = set.status !== 'draft' || !isAdmin;

  const patch = (i, change) => setRows(rs => rs.map((r, idx) => idx === i ? { ...r, ...change } : r));

  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.put(`/migration/mappings/${set.set_id}`, { rows, workflow });
      toast.success(data.outstanding?.length
        ? `Saved — ${data.outstanding.length} thing${data.outstanding.length > 1 ? 's' : ''} still to decide`
        : 'Saved — this map is ready for a dry run');
      onSaved(data);
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : d?.message || 'Could not save the map');
    }
    setBusy(false);
  };

  return (
    <div className="space-y-3" data-testid="mapping-editor">
      <Card className="rounded-md bg-white p-4 overflow-x-auto" style={{ border: '1px solid rgba(136,150,168,0.12)' }}>
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-xs font-semibold uppercase tracking-wider" style={{ color: C.slate }}>
            Field map · {set.label}
          </h3>
          {!frozen && (
            <Button size="sm" onClick={save} disabled={busy} className="text-[11px] rounded-sm h-7"
              style={{ background: C.navy }} data-testid="mapping-save-btn">
              {busy ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Save className="w-3 h-3 mr-1" />}Save map
            </Button>
          )}
        </div>
        <table className="w-full text-[11px]" data-testid="mapping-rows-table">
          <thead><tr className="text-left text-[10px] uppercase" style={{ color: C.slate }}>
            <th className="py-1 pr-2">Canonical field</th><th className="py-1 pr-2">Source column</th>
            <th className="py-1 pr-2">Transformation</th><th className="py-1 pr-2">Detail</th>
            <th className="py-1 pr-2">Default</th><th className="py-1">Waive</th>
          </tr></thead>
          <tbody>
            {rows.map((r, i) => {
              const targets = targetsFor(set.entity, r.canonical_field);
              return (
                <tr key={r.canonical_field} className="border-t align-top" data-testid={`map-row-${r.canonical_field}`}
                  style={{ borderColor: 'rgba(136,150,168,0.12)' }}>
                  <td className="py-1.5 pr-2" style={{ color: C.navy }}>
                    {r.label}{r.required && <span className="ml-1 text-[9px] font-bold" style={{ color: C.red }}>REQ</span>}
                    <div className="text-[9px]" style={{ color: C.slate }}>{r.rule}</div>
                  </td>
                  <td className="py-1.5 pr-2">
                    <select value={r.source_column || ''} disabled={frozen} className={sel} style={{ ...border, minWidth: 150 }}
                      data-testid={`source-select-${r.canonical_field}`}
                      onChange={e => patch(i, { source_column: e.target.value || null })}>
                      <option value="">— none —</option>
                      {(set.source_columns || []).map(c => <option key={c} value={c}>{c}</option>)}
                    </select>
                    {r.why && (
                      <div className="text-[9px] mt-0.5"
                        style={{ color: (!r.source_column && !r.waived && r.required) ? C.red : C.slate }}>
                        {r.why}
                      </div>
                    )}
                  </td>
                  <td className="py-1.5 pr-2">
                    <select value={r.transform} disabled={frozen} className={sel} style={{ ...border, minWidth: 130 }}
                      data-testid={`transform-select-${r.canonical_field}`}
                      onChange={e => patch(i, { transform: e.target.value, options: {} })}>
                      {transforms.map(t => <option key={t.key} value={t.key}>{t.label}</option>)}
                    </select>
                    <div className="text-[9px] mt-0.5" style={{ color: C.slate }}>
                      {(transforms.find(t => t.key === r.transform) || {}).note}
                    </div>
                  </td>
                  <td className="py-1.5 pr-2">
                    {frozen ? <span className="text-[10px]" style={{ color: C.slate }}>
                      {describe(r)}
                    </span> : <Options row={r} targets={targets} onChange={o => patch(i, { options: o })} />}
                  </td>
                  <td className="py-1.5 pr-2">
                    <Input value={r.default ?? ''} disabled={frozen} placeholder="if blank"
                      className="h-6 text-[11px] rounded-sm w-24" data-testid={`default-${r.canonical_field}`}
                      onChange={e => patch(i, { default: e.target.value || null })} />
                  </td>
                  <td className="py-1.5">
                    <label className="flex items-center gap-1 text-[10px]" style={{ color: C.slate }}>
                      <input type="checkbox" checked={!!r.waived} disabled={frozen}
                        data-testid={`waive-${r.canonical_field}`}
                        onChange={e => patch(i, { waived: e.target.checked })} />
                      not migrated
                    </label>
                    {r.waived && (
                      <Input value={r.waiver_reason || ''} disabled={frozen} placeholder="why not?"
                        className="h-6 text-[11px] rounded-sm w-32 mt-1"
                        data-testid={`waiver-reason-${r.canonical_field}`}
                        onChange={e => patch(i, { waiver_reason: e.target.value })} />
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {!!(set.unmapped_source_columns || []).length && (
          <p className="text-[10px] mt-2" style={{ color: C.slate }} data-testid="mapping-unmapped-columns">
            <CircleSlash className="w-3 h-3 inline mr-1" />
            Source columns with no canonical home: {set.unmapped_source_columns.join(', ')} — either they
            map to a field above, or the migration does not carry them.
          </p>
        )}
      </Card>

      {workflow && (
        <Card className="rounded-md bg-white p-4" style={{ border: '1px solid rgba(136,150,168,0.12)' }}
          data-testid="workflow-card">
          <h3 className="text-xs font-semibold uppercase tracking-wider mb-1" style={{ color: C.slate }}>
            Workflow map · {workflow.field}
          </h3>
          <p className="text-[11px] mb-2" style={{ color: C.slate }}>{workflow.note}</p>
          <table className="w-full text-[11px]" data-testid="workflow-table">
            <thead><tr className="text-left text-[10px] uppercase" style={{ color: C.slate }}>
              <th className="py-1 pr-2">Source state</th><th className="py-1 pr-2">Rows</th>
              <th className="py-1 pr-2">Platform state</th><th className="py-1 pr-2">What that state starts</th>
              <th className="py-1">Not migrated</th>
            </tr></thead>
            <tbody>
              {(workflow.states || []).map((s, i) => {
                const states = targetsFor(set.entity, '__workflow__');
                const target = states.find(t => t.value === s.canonical_state);
                return (
                  <tr key={s.source_state} className="border-t align-top" style={{ borderColor: 'rgba(136,150,168,0.12)' }}
                    data-testid={`workflow-row-${s.source_state}`}>
                    <td className="py-1.5 pr-2 font-mono" style={{ color: C.navy }}>{s.source_state}</td>
                    <td className="py-1.5 pr-2" style={{ color: C.slate }}>{(s.rows || 0).toLocaleString()}</td>
                    <td className="py-1.5 pr-2">
                      <select value={s.canonical_state || ''} disabled={frozen} className={sel} style={{ ...border, minWidth: 160 }}
                        data-testid={`workflow-select-${s.source_state}`}
                        onChange={e => setWorkflow(w => ({
                          ...w, states: w.states.map((x, idx) => idx === i ? { ...x, canonical_state: e.target.value || null } : x)
                        }))}>
                        <option value="">— decide —</option>
                        {states.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                      </select>
                    </td>
                    <td className="py-1.5 pr-2 text-[10px]" style={{ color: target?.starts?.length ? C.navy : C.slate }}>
                      {target ? (target.starts?.length ? target.starts.join(' · ') : 'nothing — terminal state') : '—'}
                    </td>
                    <td className="py-1.5">
                      <input type="checkbox" checked={!!s.waived} disabled={frozen}
                        data-testid={`workflow-waive-${s.source_state}`}
                        onChange={e => setWorkflow(w => ({
                          ...w, states: w.states.map((x, idx) => idx === i ? { ...x, waived: e.target.checked } : x)
                        }))} />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {!frozen && (
            <Button size="sm" onClick={save} disabled={busy} className="text-[11px] rounded-sm h-7 mt-2"
              style={{ background: C.navy }} data-testid="workflow-save-btn">
              {busy ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Save className="w-3 h-3 mr-1" />}Save map
            </Button>
          )}
        </Card>
      )}
    </div>
  );
}
